<script setup lang="ts">
import { computed, ref } from 'vue';
import { UiButton, UiChip, UiText } from '@shopexperts/shared';
import ExpertsSmartFilterDialog from './ExpertsSmartFilterDialog.vue';
import { useExpertFacets } from '../../composables/useExpertFacets';
import { useBadgeBySlug } from '../../composables/useBadgeBySlug';
import { useCitiesByIds } from '../../composables/useCityLookup';
import {
  countActiveSmartFilters,
  parseExpertsSmartFilterValue,
  serializeExpertsSmartFilterValue,
  LIST_STATE_KEYS,
  type ListStateKey,
} from '../../utils/expertsSmartFilter';

const props = defineProps<{
  name: string;
  hint?: string;
  error?: string;
}>();

const model = defineModel<string>({ default: '' });

const isDialogOpen = ref(false);
const { labelByFilterValue } = useExpertFacets();

const state = computed(() => parseExpertsSmartFilterValue(model.value));
const activeCount = computed(() => countActiveSmartFilters(state.value));
const { badgeName: savedBadgeName } = useBadgeBySlug(computed(() => state.value.partner_badge));
const { labelById: cityLabelById } = useCitiesByIds(computed(() => state.value.cities));

const LIST_GROUP_LABELS: Record<ListStateKey, string> = {
  services_offered: 'Services',
  apps_and_themes: 'Apps & themes',
  countries: 'Location',
  cities: 'City',
  languages: 'Languages',
  partner_tiers: 'Partner tier',
};

interface SummaryChip {
  key: string;
  label: string;
  remove: () => void;
}

function commit(mutate: (draft: ReturnType<typeof parseExpertsSmartFilterValue>) => void) {
  const draft = parseExpertsSmartFilterValue(model.value);
  mutate(draft);
  model.value = serializeExpertsSmartFilterValue(draft);
}

const summaryChips = computed<SummaryChip[]>(() => {
  const chips: SummaryChip[] = [];
  const current = state.value;
  const labels = labelByFilterValue.value;

  if (current.expert_type) {
    chips.push({
      key: 'expert_type',
      label: `Type: ${labels.expert_type[current.expert_type] || current.expert_type}`,
      remove: () => commit((draft) => { draft.expert_type = ''; }),
    });
  }

  for (const key of LIST_STATE_KEYS) {
    for (const value of current[key]) {
      chips.push({
        key: `${key}:${value}`,
        label: `${LIST_GROUP_LABELS[key]}: ${labels[key]?.[value] || (key === 'cities' ? cityLabelById.value.get(value) : undefined) || value}`,
        remove: () => commit((draft) => { draft[key] = draft[key].filter(entry => entry !== value); }),
      });
    }
  }

  if (current.partner_badge) {
    chips.push({
      key: 'partner_badge',
      label: `Badge: ${savedBadgeName.value || current.partner_badge}`,
      remove: () => commit((draft) => { draft.partner_badge = ''; }),
    });
  }

  if (current.price_from || current.price_to) {
    const from = current.price_from ? `$${Number(current.price_from).toLocaleString()}` : '$0';
    const to = current.price_to ? `$${Number(current.price_to).toLocaleString()}` : 'any';
    chips.push({
      key: 'price',
      label: `Budget: ${from} – ${to}`,
      remove: () => commit((draft) => { draft.price_from = ''; draft.price_to = ''; }),
    });
  }

  if (current.search) {
    chips.push({
      key: 'search',
      label: `Search: ${current.search}`,
      remove: () => commit((draft) => { draft.search = ''; }),
    });
  }

  if (current.is_pro) {
    chips.push({
      key: 'is_pro',
      label: 'Pro experts only',
      remove: () => commit((draft) => { draft.is_pro = false; }),
    });
  }

  if (current.ranking) {
    chips.push({
      key: 'ranking',
      label: 'Ranked by most recent',
      remove: () => commit((draft) => { draft.ranking = ''; }),
    });
  }

  return chips;
});

function clearAll() {
  model.value = '';
}

function onApply(value: string) {
  model.value = value;
}
</script>

<template>
  <div class="flex flex-col gap-2">
    <div class="flex flex-col gap-2 rounded-lg border border-se-gray-200 p-3" :class="{ 'border-se-danger': Boolean(props.error) }">
      <div v-if="summaryChips.length" class="flex flex-wrap items-center gap-1.5">
        <button
          v-for="chip in summaryChips"
          :key="chip.key"
          type="button"
          class="cursor-pointer"
          :title="`Remove ${chip.label}`"
          @click="chip.remove()"
        >
          <UiChip :text="chip.label" size="tag" variant="outline" icon-after icon="close" />
        </button>
      </div>
      <UiText
        v-else
        as="p"
        variant="caption-normal"
        class="text-se-gray-500"
        text="No filters selected — this collection would show all experts."
      />
      <div class="flex items-center gap-2">
        <UiButton
          :label="activeCount ? 'Edit filters' : 'Choose filters'"
          variant="outline"
          size="sm"
          :name="`${props.name}-choose-filters`"
          @click="isDialogOpen = true"
        />
        <UiButton
          v-if="activeCount"
          label="Clear all"
          variant="ghost"
          size="sm"
          hover-style="highlight"
          @click="clearAll"
        />
      </div>
    </div>

    <UiText v-if="props.error" as="p" variant="caption-normal" class="text-se-danger" :text="props.error" />
    <UiText v-else-if="props.hint" as="p" variant="caption-normal" class="text-se-gray-500" :text="props.hint" />

    <!-- v-if unmounts the teleported dialog DOM on close; the shared UiDialog
         lazy-mounts but never unmounts, which leaves a full-screen overlay. -->
    <ExpertsSmartFilterDialog v-if="isDialogOpen" v-model:open="isDialogOpen" :value="model" @apply="onApply" />
  </div>
</template>
